import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { Plan } from '../../../../core/models/plan.model';
import { EmpresaFormValue } from '../../../../core/models/empresa.data.model';
import { SelectOption, SelectComponent } from '../../../../shared/select/select';
import { FORM_LIMITS, isValidDateInput } from '../../../../core/utils/validation.utils';

interface FormModel {
  razonSocial: string;
  identificacionFiscal: string;
  planId: string;
  fechaInicio: string;
  fechaFin: string;
  estado: boolean;
}

@Component({
  selector: 'app-empresas-form',
  standalone: true,
  imports: [FormsModule, SelectComponent],
  templateUrl: './empresas-form.html',
  styleUrl: './empresas-form.css',
})
export class EmpresasFormComponent {
  @Input() empresa: EmpresaFormValue | null = null;
  @Input() planes: Plan[] = [];
  @Input() serverError = '';
  @Output() readonly saved = new EventEmitter<EmpresaFormValue>();
  @Output() readonly cancelled = new EventEmitter<void>();

  protected readonly form: FormModel = this.emptyForm();
  protected error = '';

  protected get planOptions(): SelectOption[] {
    return this.planes.map((plan) => ({
      label: `${plan.name}${plan.isActive ? '' : ' (inactivo)'}`,
      value: plan.id,
      disabled: !plan.isActive,
    }));
  }

  ngOnChanges(): void {
    Object.assign(this.form, {
      razonSocial: this.empresa?.razonSocial ?? '',
      identificacionFiscal: this.empresa?.identificacionFiscal ?? '',
      planId: this.empresa?.planId ?? '',
      fechaInicio: this.empresa ? this.toDateInput(this.empresa.fechaInicio) : this.today(),
      fechaFin: this.toDateInput(this.empresa?.fechaFin),
      estado: this.empresa?.estado ?? true,
    });
    this.error = '';
  }

  protected submit(): void {
    if (
      !this.form.razonSocial.trim() ||
      !this.form.identificacionFiscal.trim() ||
      !this.form.planId
    ) {
      this.error = 'La razón social, identificación fiscal y plan son obligatorios.';
      return;
    }

    if (!this.form.fechaInicio) {
      this.error = 'La fecha de inicio es obligatoria.';
      return;
    }
    if (
      !isValidDateInput(this.form.fechaInicio) ||
      (this.form.fechaFin && !isValidDateInput(this.form.fechaFin))
    ) {
      this.error = 'Las fechas ingresadas no son válidas.';
      return;
    }
    if (
      this.form.razonSocial.trim().length > FORM_LIMITS.companyNameMaxLength ||
      !/^\d{10,20}$/.test(this.form.identificacionFiscal.trim())
    ) {
      this.error = 'Revisa la longitud de la razón social y la identificación fiscal.';
      return;
    }

    if (this.form.fechaFin && this.form.fechaFin < this.form.fechaInicio) {
      this.error = 'La fecha de finalización no puede ser anterior a la fecha de inicio.';
      return;
    }

    this.error = '';
    this.saved.emit({
      idEmpresa: this.empresa?.idEmpresa,
      razonSocial: this.form.razonSocial.trim(),
      identificacionFiscal: this.form.identificacionFiscal.trim(),
      planId: this.form.planId,
      fechaInicio: this.form.fechaInicio,
      fechaFin: this.form.fechaFin || null,
      estado: this.form.estado ? 'ACTIVA' : 'INACTIVA',
    });
  }

  private emptyForm(): FormModel {
    return {
      razonSocial: '',
      identificacionFiscal: '',
      planId: '',
      fechaInicio: this.today(),
      fechaFin: '',
      estado: true,
    };
  }

  private toDateInput(value: string | null | undefined): string {
    return value ? value.slice(0, 10) : '';
  }

  private today(): string {
    return new Date().toISOString().slice(0, 10);
  }
}
